import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { Col2ContentFrame, SpacingFrame } from "../display/Frame";
import { TitleSubtitle } from "../display/TextFormat";
import { DisplayProductCards } from "../../functions/DisplayInfoFunctions";
import { HealthSection } from "../health/HealthSection";
import { HealthSectionHeader } from "../health/HealthSectionHeader";
import { getLocalized, HealthLang } from "../health/healthTypes";
import { healthBody } from "../health/healthVisualStyles";
import { agentsLocalePath } from "./agentsLocalePath";

interface AgentsProductSuiteSectionProps {
    data: DataKeys;
}

function renderSubtitleWithLink(subtitle: string, hLang: HealthLang) {
    const parts = subtitle.split(/\[([^\]]+)\]\(([^)]+)\)/);
    if (parts.length < 3) {
        return subtitle;
    }
    const nodes: ReactNode[] = [];
    for (let i = 0; i < parts.length; i += 3) {
        if (parts[i]) nodes.push(parts[i]);
        if (parts[i + 1] && parts[i + 2]) {
            nodes.push(
                <Link
                    key={parts[i + 1]}
                    to={agentsLocalePath(parts[i + 2], hLang)}
                    className="font-medium text-teal-600 hover:text-teal-700 hover:underline"
                >
                    {parts[i + 1]}
                </Link>
            );
        }
    }
    return nodes;
}

export function AgentsProductSuiteSection({ data }: AgentsProductSuiteSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const sectionId = (data?.id as string) || "product-suite";
    const groups: DataKeys[] = data?.groups ?? [];
    const subtitle = getLocalized(data?.subtitle, hLang);

    return (
        <HealthSection id={sectionId} ariaLabelledBy={`${sectionId}-title`} compact>
            <HealthSectionHeader
                eyebrow={getLocalized(data?.eyebrow, hLang)}
                title={getLocalized(data?.title, hLang)}
                titleId={`${sectionId}-title`}
                subtitle={undefined}
            />
            {subtitle && (
                <p className={`${healthBody} mt-3 max-w-3xl text-sm text-grey-500`}>
                    {renderSubtitleWithLink(subtitle, hLang)}
                </p>
            )}
            <div className="mt-20 flex flex-col lg:mt-24">
                {groups.map((group, index) => {
                    const solutions = (group.solutions as string[]) ?? [];
                    return (
                        <div
                            key={group.id as string}
                            className={index > 0 ? "mt-16 lg:mt-24" : undefined}
                        >
                            <SpacingFrame spacing={48} center>
                                <div className="w-full text-center">
                                    <TitleSubtitle
                                        title={getLocalized(group.title, hLang)}
                                    />
                                </div>
                                <Col2ContentFrame>
                                    <DisplayProductCards data={solutions} />
                                </Col2ContentFrame>
                            </SpacingFrame>
                        </div>
                    );
                })}
            </div>
        </HealthSection>
    );
}
